import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { getTrackerTagsApi } from "../../../DAL/trackerTag/trackerTag";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const PRIMARY = "var(--portal-theme-primary)";

/**
 * Pick tags for a tracker from the shared library (no free-text create).
 * Value stays string[] on the tracker for backward compatibility.
 */
export default function TrackerLibraryTagPicker({
  value = [],
  onChange,
  description = "Select tags from the shared library. These are available on submissions for filters and automations — not auto-applied when someone joins.",
}) {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);
  const selected = Array.isArray(value) ? value : [];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getTrackerTagsApi({ limit: 500, status: "active" });
        if (!cancelled) {
          setLibrary(Array.isArray(res?.tags) ? res.tags : []);
        }
      } catch (_e) {
        if (!cancelled) setLibrary([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const names = (library || [])
      .map((t) => String(t?.name || "").trim())
      .filter(Boolean);
    // Keep any already-selected tags that may be inactive / legacy free-text.
    selected.forEach((name) => {
      if (
        name &&
        !names.some((n) => n.toLowerCase() === String(name).toLowerCase())
      ) {
        names.push(name);
      }
    });
    return names;
  }, [library, selected]);

  const colorByName = useMemo(() => {
    const map = {};
    (library || []).forEach((t) => {
      if (t?.name) map[String(t.name).toLowerCase()] = t.color || PRIMARY;
    });
    return map;
  }, [library]);

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: MUTED, mb: 1.25 }}>
        {description}{" "}
        <Box
          component={RouterLink}
          to="/tracker-tags"
          sx={{
            color: PRIMARY,
            fontWeight: 700,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Manage tags
        </Box>
      </Typography>

      <Autocomplete
        multiple
        options={options}
        value={selected}
        loading={loading}
        disableCloseOnSelect
        onChange={(_e, next) => onChange?.(next)}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((option, index) => {
            const color = colorByName[String(option).toLowerCase()] || PRIMARY;
            const props = getTagProps({ index });
            return (
              <Chip
                {...props}
                key={option}
                label={option}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: "#fff",
                  border: `1px solid ${BORDER}`,
                  "& .MuiChip-avatar": { display: "none" },
                  "&::before": {
                    content: '""',
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: color,
                    mr: 0.75,
                    display: "inline-block",
                  },
                }}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label="Select tags"
            placeholder={selected.length ? "" : "Search library tags…"}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={
          loading
            ? "Loading…"
            : "No tags yet — create some under Trackers → Tags"
        }
        sx={{ maxWidth: 560 }}
      />
    </Box>
  );
}
